import { useEffect, useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { CALENDLY_URL } from './constants'
import { useLanguage } from '@/i18n/LanguageContext'

type Layer = {
  name: 'firenze' | 'sfondo' | 'ragazza'
  src: string
  zIndex: number
  speed?: number
  sizes: string
}

const LAYERS: Layer[] = [
  { name: 'firenze', src: '/videos/firenze.mp4', zIndex: 0, speed: 0.1, sizes: '18vw' },
  { name: 'sfondo', src: '/images/sfondo.png?v=2', zIndex: 10, sizes: '100vw' },
  { name: 'ragazza', src: '/images/francesca.png', zIndex: 20, speed: 0.32, sizes: '26vw' },
]

const speedOf = (name: Layer['name']) => LAYERS.find((l) => l.name === name)?.speed ?? 0

export default function ParallaxHero() {
  const { t } = useLanguage()
  const heroRef = useRef<HTMLElement>(null)
  const firenzeRef = useRef<HTMLDivElement>(null)
  const ragazzaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const hero = heroRef.current
    if (!hero) return

    const moving: Array<[React.RefObject<HTMLDivElement>, number]> = [
      [firenzeRef, speedOf('firenze')],
      [ragazzaRef, speedOf('ragazza')],
    ]

    const MAX_SHIFT = 120
    let raf = 0
    let targetX = 0
    let currentX = 0
    let animating = false

    const animate = () => {
      currentX += (targetX - currentX) * 0.07
      moving.forEach(([ref, speed]) => {
        const el = ref.current
        if (el) el.style.transform = `translate3d(${-currentX * speed * MAX_SHIFT}px, 0, 0)`
      })
      if (Math.abs(targetX - currentX) > 0.001) {
        raf = requestAnimationFrame(animate)
      } else {
        animating = false
      }
    }

    const start = () => {
      if (!animating) {
        animating = true
        cancelAnimationFrame(raf)
        raf = requestAnimationFrame(animate)
      }
    }

    const onMouseMove = (e: MouseEvent) => {
      if (window.innerWidth < 1024) return
      targetX = (e.clientX / window.innerWidth) * 2 - 1
      start()
    }

    const onMouseLeave = () => {
      targetX = 0
      start()
    }

    window.addEventListener('mousemove', onMouseMove, { passive: true })
    hero.addEventListener('mouseleave', onMouseLeave)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      hero.removeEventListener('mouseleave', onMouseLeave)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden bg-[#F3E5D7] lg:h-[calc(100svh-var(--header-height,5rem))] lg:min-h-[640px] lg:bg-[#ECDBC9]"
      aria-label={t.hero.titleBefore + ' ' + t.hero.titleAccent}
    >
      <div className="px-6 pb-4 pt-32 lg:hidden">
        <h1 className="font-display text-[clamp(2.2rem,9vw,3rem)] font-medium leading-[1.08] text-ink">
          <span className="block">{t.hero.titleBefore}</span>
          <span className="block italic text-terracotta">{t.hero.titleAccent}</span>
        </h1>
      </div>

      <div className="relative aspect-[5/4] overflow-hidden lg:absolute lg:inset-0 lg:aspect-auto">
        <div className="absolute bottom-0 right-0 aspect-[1920/1043] w-[180%] lg:w-[max(100%,calc(max(100svh_-_var(--header-height,5rem),640px)*1920/1043))]">
          <div
            className="absolute overflow-hidden"
            style={{ left: '61.9%', top: '24.1%', width: '17.4%', height: '48.8%', zIndex: LAYERS[0].zIndex }}
          >
            <div ref={firenzeRef}>
              <video
                src={LAYERS[0].src}
                poster="/images/firenze.jpg"
                aria-hidden="true"
                autoPlay
                muted
                loop
                playsInline
                className="-ml-[7.5%] h-[125%] w-[115%] max-w-none object-cover object-center"
              />
            </div>
          </div>

          <img
            src={LAYERS[1].src}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ zIndex: LAYERS[1].zIndex }}
            loading="eager"
            sizes={LAYERS[1].sizes}
          />

          <div
            ref={ragazzaRef}
            className="absolute"
            style={{ left: '41.5%', bottom: 0, width: '26.04%', zIndex: LAYERS[2].zIndex }}
          >
            <img
              src={LAYERS[2].src}
              alt={t.hero.girlAlt}
              className="h-auto w-full"
              loading="lazy"
              sizes={LAYERS[2].sizes}
            />
          </div>
        </div>
      </div>

      <div className="px-6 pb-10 pt-8 lg:hidden">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">
          {t.hero.eyebrow}
        </p>
        <p className="mt-3 font-display text-2xl font-medium text-ink">
          {t.hero.greetingBefore}
          <span className="italic text-terracotta">{t.hero.greetingAccent}</span>
        </p>
        <p className="mt-4 text-base leading-relaxed text-ink/75">{t.hero.paragraphMobile}</p>
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 flex w-full items-center justify-center gap-3 whitespace-nowrap rounded-full bg-terracotta px-8 py-4 text-lg font-semibold text-cream shadow-lg shadow-terracotta/30"
        >
          {t.common.ctaLabel}
          <ArrowRight className="h-5 w-5" />
        </a>
        <p className="mt-4 text-center text-sm text-ink/60">{t.common.freeNote}</p>
      </div>

      <div className="absolute left-[9.4%] top-[57%] z-30 hidden max-w-[min(560px,40%)] -translate-y-1/2 lg:block lg:max-w-[min(560px,34%)]">
        <p className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.25em] text-terracotta lg:text-sm">
          {t.hero.eyebrow}
        </p>
        <h1 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-ink lg:text-6xl">
          <span className="block">{t.hero.titleBefore}</span>
          <span className="block italic text-terracotta">{t.hero.titleAccent}</span>
        </h1>
        <p className="mt-8 text-base leading-relaxed text-ink/75 lg:text-lg">
          {t.hero.paragraph}
        </p>
        <div className="mt-10 inline-flex flex-col items-center">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-full bg-terracotta px-8 py-4 text-lg font-semibold text-cream shadow-lg shadow-terracotta/30 transition-transform duration-200 hover:scale-105"
          >
            {t.common.ctaLabel}
            <ArrowRight className="h-5 w-5" />
          </a>
          <p className="mt-4 text-center text-sm text-ink/60">{t.common.freeNote}</p>
        </div>
      </div>
    </section>
  )
}
